import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface TelefonSecenegi {
    numara: string;
    etiket: string; // 'Telefon 1', 'Telefon 2', vb.
}

interface SmsSablon {
    sablonID: number;
    sablonAdi: string;
    sablonIcerigi: string;
}

interface SmsGonderModalProps {
    onClose: () => void;
    onSent?: () => void;
    telefonlar: TelefonSecenegi[];
    cariID?: number;
    leadID?: number;
    musteriAdi?: string;
}

const SmsGonderModal: React.FC<SmsGonderModalProps> = ({
    onClose, onSent, telefonlar, cariID, leadID, musteriAdi
}) => {
    const { t } = useTranslation();
    const [smsAktif, setSmsAktif] = useState<boolean | null>(null);
    const [seciliTelefon, setSeciliTelefon] = useState('');
    const [mesaj, setMesaj] = useState('');
    const [sablonlar, setSablonlar] = useState<SmsSablon[]>([]);
    const [gonderiliyor, setGonderiliyor] = useState(false);

    useEffect(() => {
        // SMS servis kontrolü
        axios.get('/api/SmsGonder/aktif')
            .then(r => setSmsAktif(r.data.aktif))
            .catch(() => setSmsAktif(false));

        // SMS şablonlarını yükle
        axios.get('/api/SmsSablonlari')
            .then(r => setSablonlar(r.data))
            .catch(() => {});

        // İlk telefonu seç
        if (telefonlar.length > 0) setSeciliTelefon(telefonlar[0].numara);
    }, []);

    const handleSablonSec = (sablonIcerigi: string) => {
        setMesaj(sablonIcerigi);
    };

    const handleGonder = async () => {
        if (!seciliTelefon) { toast.warning(t('msg.uyari')); return; }
        if (!mesaj.trim()) { toast.warning(t('msg.uyari')); return; }

        setGonderiliyor(true);
        try {
            const res = await axios.post('/api/SmsGonder', {
                telefon: seciliTelefon,
                mesaj: mesaj.trim(),
                cariID: cariID ?? null,
                leadID: leadID ?? null,
            });
            if (res.data.basarili) {
                toast.success(res.data.message);
                onSent?.();
                onClose();
            } else {
                toast.error(res.data.message);
            }
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setGonderiliyor(false);
        }
    };

    const kalanKarakter = 160 - mesaj.length;

    return (
        /* Overlay */
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{
                background: '#fff', borderRadius: 14, width: 480, maxWidth: '95vw',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
            }}>
                {/* Başlık */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <i className="fas fa-sms" style={{ color: '#0ea5e9' }} />
                            SMS Gönder
                        </div>
                        {musteriAdi && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{musteriAdi}</div>}
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#94a3b8', fontSize: 18, padding: '4px 8px'
                    }}><i className="fas fa-times" /></button>
                </div>

                <div style={{ padding: 20 }}>
                    {/* SMS Pasif Uyarısı */}
                    {smsAktif === false && (
                        <div style={{
                            background: '#fef9c3', border: '1px solid #fde047',
                            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                            display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13
                        }}>
                            <i className="fas fa-exclamation-triangle" style={{ color: '#ca8a04', marginTop: 2 }} />
                            <div>
                                <strong>SMS servisi pasif durumda.</strong> Gönderim yapabilmek için
                                <strong> Ayarlar &gt; SMS Ayarları</strong> bölümünden servisi etkinleştirin.
                            </div>
                        </div>
                    )}

                    {/* Telefon Seçimi */}
                    <div className="mb-3">
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>
                            TELEFON NUMARASI
                        </label>
                        {telefonlar.length === 0 ? (
                            <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>
                                <i className="fas fa-exclamation-circle me-2" />Bu kayıta ait telefon numarası bulunamadı.
                            </div>
                        ) : (
                            <select
                                className="form-select form-select-sm"
                                value={seciliTelefon}
                                onChange={e => setSeciliTelefon(e.target.value)}
                                style={{ fontSize: 13 }}
                            >
                                {telefonlar.map(item => (
                                    <option key={item.numara} value={item.numara}>
                                        {item.etiket}: {item.numara}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Şablon Seçimi */}
                    {sablonlar.length > 0 && (
                        <div className="mb-3">
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>
                                ŞABLONDAN DOLDUR <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opsiyonel)</span>
                            </label>
                            <select
                                className="form-select form-select-sm"
                                defaultValue=""
                                onChange={e => { if (e.target.value) handleSablonSec(e.target.value); }}
                                style={{ fontSize: 13 }}
                            >
                                <option value="">-- Şablon Seçin --</option>
                                {sablonlar.map(s => (
                                    <option key={s.sablonID} value={s.sablonIcerigi}>{s.sablonAdi}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Mesaj */}
                    <div className="mb-3">
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>
                            MESAJ
                        </label>
                        <textarea
                            className="form-control"
                            rows={5}
                            maxLength={1600}
                            value={mesaj}
                            onChange={e => setMesaj(e.target.value)}
                            placeholder={t('label.ara')}
                            style={{ fontSize: 13, resize: 'vertical' }}
                        />
                        <div style={{ textAlign: 'right', fontSize: 11, color: kalanKarakter < 20 ? '#ef4444' : '#94a3b8', marginTop: 4 }}>
                            {mesaj.length} / 160 karakter
                            {mesaj.length > 160 && <span style={{ color: '#f59e0b', marginLeft: 8 }}>
                                ({Math.ceil(mesaj.length / 160)} SMS)
                            </span>}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #f1f5f9',
                    display: 'flex', justifyContent: 'flex-end', gap: 8
                }}>
                    <button type="button" onClick={onClose}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>
                        İptal
                    </button>
                    <button
                        type="button"
                        onClick={handleGonder}
                        disabled={gonderiliyor || telefonlar.length === 0 || !mesaj.trim()}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: 'none',
                            background: gonderiliyor || telefonlar.length === 0 || !mesaj.trim() ? '#94a3b8' : '#0ea5e9',
                            color: '#fff', fontSize: 13, fontWeight: 600, cursor: gonderiliyor ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6
                        }}
                    >
                        {gonderiliyor
                            ? <><i className="fas fa-spinner fa-spin" /> Gönderiliyor...</>
                            : <><i className="fas fa-paper-plane" /> Gönder</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SmsGonderModal;
